import * as React from "react";
import { AlertCircle, ShieldQuestion } from "lucide-react";
import {
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  userHasPermission,
  type StudentImportPreviewResponse,
  type StudentImportSummary,
} from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";
import {
  DoneCard,
  PreviewCard,
  TemplateCard,
  UploadDropzone,
  MAX_FILE_BYTES,
  formatBytes,
  messageFor,
} from "@/components/student-imports";
import {
  useStandingStudentImportCommit,
  useStandingStudentImportPreview,
  useStandingStudentImportTemplateInfo,
} from "@/hooks/useStandingStudentImports";
import { useEnabledModules } from "@/hooks/useEnabledModules";
import { getVerticalProfile, importPeopleLabelFor } from "@/lib/verticalProfile";
import { api } from "@/lib/api";

/**
 * Phase H6d — standing post-first-run student importer.
 *
 * Mounted at `/imports/students` inside the AppShell. Wraps the H6b
 * BE standing surface (`/me/students/{import-template,preview,commit}`)
 * with the same Upload → Preview → Done flow as the wizard's step 3.
 * Differences vs `FirstRunImportStudentsPage`:
 *
 *   - No FirstRunShell chrome, no "Step 3 of 3" eyebrow, no
 *     "Skip — start fresh" exit. Lives inside the AppShell so the
 *     admin keeps their sidebar + topbar context.
 *   - Done card shows an "Import another batch" CTA that resets the
 *     page to the Upload state (instead of completing first-run +
 *     navigating to /dashboard).
 *   - Permission-gated on the new `import_students` permission. Users
 *     without it (e.g. ungranted staff users with no override) see a
 *     friendly explanation card instead of the importer surface.
 *   - The template-download button hits `/me/students/import-template.csv`
 *     (the standing route) rather than `/me/first-run/students/template.csv`
 *     — same response shape, separate guards (admin tenant scope vs
 *     wizard owner) on the BE.
 *
 * The shared visual primitives (`TemplateCard`, `UploadDropzone`,
 * `PreviewCard`, `DoneCard`) live in `components/student-imports/`
 * so the wizard and this page never drift in copy or layout.
 */
export function ImportStudentsPage() {
  const { user } = useAuth();
  const canImport = user
    ? userHasPermission(
        user.role,
        user.permission_overrides ?? [],
        "import_students",
        user.permission_mode ?? "additive",
      )
    : false;
  // Phase SC-3c — the wizard's version of this step has been
  // vertical-aware since C1c ("Import learners"); the standing page and
  // its nav row had not caught up, so a study-centre owner reached
  // "Import students" from a sidebar that everywhere else says Learners.
  const { vertical } = useEnabledModules();
  const profile = getVerticalProfile(vertical ?? "school");
  const importLabel = importPeopleLabelFor(profile);
  const personMany = profile.personNoun.many.toLowerCase();

  const [file, setFile] = React.useState<File | null>(null);
  const [preview, setPreview] = React.useState<StudentImportPreviewResponse | null>(null);
  const [topLevelError, setTopLevelError] = React.useState<string | null>(null);
  const [doneSummary, setDoneSummary] = React.useState<{
    summary: StudentImportSummary;
    batch_id: string;
  } | null>(null);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const templateQuery = useStandingStudentImportTemplateInfo();
  const previewMutation = useStandingStudentImportPreview();
  const commitMutation = useStandingStudentImportCommit();

  const [templateDownloadError, setTemplateDownloadError] = React.useState<string | null>(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = React.useState(false);

  const handleDownloadTemplate = React.useCallback(async () => {
    setTemplateDownloadError(null);
    setIsDownloadingTemplate(true);
    try {
      // Standing endpoint — the wizard's `/me/first-run/students/template.csv`
      // requires the principal still to be in first-run, so it 403s here.
      const response = await api.raw.get<Blob>("/me/students/import-template.csv", {
        responseType: "blob",
      });
      const blob = response.data;
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = `${personMany}-template.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      const apiErr = err instanceof ApiError ? err : null;
      setTemplateDownloadError(
        apiErr?.message ?? (err instanceof Error ? err.message : "Failed to download template."),
      );
    } finally {
      setIsDownloadingTemplate(false);
    }
  }, [personMany]);

  const resetSelection = () => {
    setFile(null);
    setPreview(null);
    setTopLevelError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const resetToUpload = () => {
    setDoneSummary(null);
    resetSelection();
  };

  const handleFile = async (chosen: File) => {
    setTopLevelError(null);
    setPreview(null);
    if (chosen.size > MAX_FILE_BYTES) {
      setTopLevelError(
        `File is ${formatBytes(chosen.size)} — max 1 MB. Split into smaller batches.`,
      );
      return;
    }
    if (chosen.size === 0) {
      setTopLevelError("That file is empty. Pick a CSV with at least a header row.");
      return;
    }
    setFile(chosen);
    try {
      const result = await previewMutation.mutateAsync({ file: chosen });
      setPreview(result);
    } catch (err) {
      setTopLevelError(messageFor(err));
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0];
    if (chosen) {
      void handleFile(chosen);
    }
  };

  const onDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    const dropped = e.dataTransfer?.files?.[0];
    if (dropped) {
      void handleFile(dropped);
    }
  };

  const onCommit = async () => {
    if (!file) return;
    setTopLevelError(null);
    try {
      const result = await commitMutation.mutateAsync({
        file,
        skipDuplicates: true,
      });
      setDoneSummary({ summary: result.summary, batch_id: result.batch_id });
    } catch (err) {
      setTopLevelError(messageFor(err));
    }
  };

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>{importLabel}</PageHeaderHeading>
            <PageHeaderDescription>
              Bulk-add {personMany} from a CSV roster. We&apos;ll preview every row, flag
              duplicates, and only write the fresh ones.
            </PageHeaderDescription>
          </PageHeaderTitle>
        </PageHeaderTop>
      </PageHeader>

      {!canImport ? (
        <PermissionDeniedCard importLabel={importLabel} personMany={personMany} />
      ) : doneSummary ? (
        <DoneCard summary={doneSummary.summary} onReset={resetToUpload} />
      ) : preview ? (
        <PreviewCard
          file={file}
          preview={preview}
          template={templateQuery.data ?? null}
          onCommit={onCommit}
          onReset={resetSelection}
          isCommitting={commitMutation.isPending}
        />
      ) : (
        <div className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 mt-8 grid gap-6 motion-safe:duration-500">
          <TemplateCard
            template={templateQuery.data ?? null}
            onDownload={handleDownloadTemplate}
            isDownloading={isDownloadingTemplate}
            downloadError={templateDownloadError}
            bodyCopy={
              <>
                Download the recommended template — match the columns your roster already uses, then
                drop the filled-in file below.
              </>
            }
          />
          <UploadDropzone
            fileInputRef={fileInputRef}
            isPreviewPending={previewMutation.isPending}
            onFileInputChange={onFileInputChange}
            onDrop={onDrop}
            inputId="standing-student-import-file"
          />
        </div>
      )}

      {topLevelError && canImport ? (
        <div
          role="alert"
          className="border-destructive/50 bg-destructive/5 text-destructive mt-6 flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
        >
          <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>{topLevelError}</span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Friendly explainer for users without `import_students`. Mirrors the
 * tone of `TenantAuthoringPausedBanner` (H5d) but for a permission-
 * level denial rather than a tenant-flag denial. Doesn't try to teach
 * the user about the BE permission system — just explains what they
 * need to do next.
 */
function PermissionDeniedCard({
  importLabel,
  personMany,
}: {
  importLabel: string;
  personMany: string;
}) {
  return (
    <div
      role="region"
      aria-label="Permission required"
      className="motion-safe:animate-in motion-safe:fade-in mt-8 rounded-lg border border-amber-500/30 bg-amber-500/5 p-6 motion-safe:duration-300"
      data-testid="import-students-permission-denied"
    >
      <div className="flex items-start gap-3">
        <ShieldQuestion
          className="mt-0.5 size-5 shrink-0 text-amber-700 dark:text-amber-300"
          aria-hidden="true"
        />
        <div className="grid gap-1.5">
          <p className="text-foreground text-sm font-medium">
            You don&apos;t have the &quot;{importLabel}&quot; permission yet.
          </p>
          <p className="text-muted-foreground text-xs leading-relaxed">
            Ask an admin to add &quot;{importLabel}&quot; to your role from{" "}
            <span className="text-foreground">Settings → Members &amp; roles</span>. Admins have it
            already; others need it turned on before they can bulk-add {personMany}.
          </p>
        </div>
      </div>
    </div>
  );
}
